import { Component, OnInit, ViewChild, ElementRef, HostListener } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { environment } from '../../../environments/environment';
import { SharedModule } from '../../shared/shared.module';

const GALLERY_STORAGE_KEY = 'crz_chat_media_gallery';

interface GalleryImage {
  url:        string;
  fileName?:  string;
  title?:     string;
  subtitle?:  string;
  avatarUrl?: string | null;
  caption?:   string | null;
}

@Component({
  selector: 'app-image-preview-page',
  standalone: true,
  imports: [SharedModule],
  templateUrl: './image-preview-page.component.html',
  styleUrl: './image-preview-page.component.scss'
})
export class ImagePreviewPageComponent implements OnInit {

  @ViewChild('imgEl') imgEl!: ElementRef<HTMLImageElement>;

  imageUrl  = '';
  fileName  = '';
  title     = '';
  subtitle  = '';
  avatarUrl  = '';
  showAvatar: boolean | undefined = undefined;
  caption    = '';

  images: GalleryImage[] = [];
  currentIndex = 0;

  get hasGallery(): boolean { return this.images.length > 1; }

  scale      = 1;
  translateX = 0;
  translateY = 0;
  isDragging  = false;
  private lastX = 0;
  private lastY = 0;
  private readonly MIN_SCALE = 0.5;
  private readonly MAX_SCALE = 5;
  private readonly STEP      = 0.25;

  get transform(): string {
    return `translate(${this.translateX}px, ${this.translateY}px) scale(${this.scale})`;
  }
  get zoomPercent(): number { return Math.round(this.scale * 100); }

  constructor(private route: ActivatedRoute) {}

  ngOnInit() {
    this.route.queryParams.subscribe(params => {
      if (params['gallery'] === '1') {
        try {
          this.images = JSON.parse(localStorage.getItem(GALLERY_STORAGE_KEY) || '[]');
        } catch {
          this.images = [];
        }
        const requested = Number(params['galleryIndex']) || 0;
        this.currentIndex = Math.max(0, Math.min(requested, Math.max(this.images.length - 1, 0)));
        this.applyCurrentImage();
        return;
      }

      this.imageUrl  = params['url']       || '';
      this.fileName  = params['fileName']  || '';
      this.title     = params['title']     || '';
      this.subtitle  = params['subtitle']  || '';
      this.avatarUrl  = params['avatarUrl']  || '';
      this.showAvatar = params['showAvatar'] === 'false' ? false : undefined;
    });
  }

  private applyCurrentImage() {
    const item = this.images[this.currentIndex];
    if (!item) return;
    this.imageUrl  = item.url;
    this.fileName  = item.fileName  || '';
    this.title     = item.title     || '';
    this.subtitle  = item.subtitle  || '';
    this.avatarUrl  = item.avatarUrl || '';
    this.caption    = item.caption   || '';
    this.showAvatar = undefined;
    this.resetZoom();
  }

  goPrev() {
    if (this.currentIndex <= 0) return;
    this.currentIndex--;
    this.applyCurrentImage();
  }

  goNext() {
    if (this.currentIndex >= this.images.length - 1) return;
    this.currentIndex++;
    this.applyCurrentImage();
  }

  selectThumbnail(index: number) {
    if (index === this.currentIndex) return;
    this.currentIndex = index;
    this.applyCurrentImage();
  }

  @HostListener('window:keydown', ['$event'])
  onKeydown(event: KeyboardEvent) {
    if (!this.hasGallery) return;
    if (event.key === 'ArrowLeft')  this.goPrev();
    if (event.key === 'ArrowRight') this.goNext();
  }

  getInitials(name: string): string {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
  }

  getAvatarUrl(image: string): string {
    if (!image) return '';
    return image.startsWith('http') ? image : `${environment.fileBaseUrl}${image}`;
  }

  onWheel(event: WheelEvent) {
    event.preventDefault();
    const body     = (event.currentTarget as HTMLElement);
    const rect     = body.getBoundingClientRect();
    const cursorX  = event.clientX - rect.left - rect.width  / 2;
    const cursorY  = event.clientY - rect.top  - rect.height / 2;
    const delta    = event.deltaY < 0 ? this.STEP : -this.STEP;
    const newScale = Math.min(this.MAX_SCALE, Math.max(this.MIN_SCALE, this.scale + delta));
    const ratio    = newScale / this.scale;
    this.translateX = cursorX + (this.translateX - cursorX) * ratio;
    this.translateY = cursorY + (this.translateY - cursorY) * ratio;
    this.scale      = newScale;
  }

  zoomIn()  { this.scale = Math.min(this.MAX_SCALE, +(this.scale + this.STEP).toFixed(2)); }
  zoomOut() {
    this.scale = Math.max(this.MIN_SCALE, +(this.scale - this.STEP).toFixed(2));
    if (this.scale <= 1) this.resetZoom();
  }
  resetZoom() { this.scale = 1; this.translateX = 0; this.translateY = 0; }

  onMouseDown(event: MouseEvent) {
    if (this.scale <= 1) return;
    this.isDragging = true;
    this.lastX = event.clientX;
    this.lastY = event.clientY;
  }
  onMouseMove(event: MouseEvent) {
    if (!this.isDragging) return;
    this.translateX += event.clientX - this.lastX;
    this.translateY += event.clientY - this.lastY;
    this.lastX = event.clientX;
    this.lastY = event.clientY;
  }
  onMouseUp() { this.isDragging = false; }
}